<script setup lang="ts">
/**
 * OrganizationSwitcher Component
 * 
 * Dropdown for switching between organizations for multi-organization users.
 * Super admins see all organizations with a search filter.
 * Regular users see only their memberships.
 */
import { ref, computed, watch, nextTick, onMounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'
import { getOrganizations } from '@/modules/organizations/api/service'

defineProps<{
  collapsed?: boolean
}>()

const authStore = useAuthStore()
const uiStore = useUiStore()

const isOpen = ref(false)
const isSwitching = ref(false)
const searchQuery = ref('')
const searchInputRef = ref<HTMLInputElement | null>(null)

// All orgs fetched from API (super admin only)
const allOrganizations = ref<Array<{ id: string; name: string; isDefault?: boolean }>>([])

const currentOrganization = computed(() => authStore.currentOrganization)
const isSuperAdmin = computed(() => authStore.isSuperAdmin)

/**
 * Determine if the switcher should be visible.
 * Super admins always see it (they can access all orgs).
 * Regular users see it only with multiple memberships.
 */
const isVisible = computed(() => {
  if (isSuperAdmin.value) return true
  return authStore.hasMultipleOrganizations
})

/**
 * The base list of organizations before search filtering.
 * Super admins get allOrganizations (from API), regular users get memberships.
 */
const baseOrganizations = computed(() => {
  const orgs = isSuperAdmin.value && allOrganizations.value.length > 0
    ? allOrganizations.value
    : authStore.organizations

  // Sort alphabetically by name
  return [...orgs].sort((a, b) => a.name.localeCompare(b.name))
})

/**
 * Filtered organizations based on search query
 */
const filteredOrganizations = computed(() => {
  if (!searchQuery.value) return baseOrganizations.value
  const query = searchQuery.value.toLowerCase()
  return baseOrganizations.value.filter(org =>
    org.name.toLowerCase().includes(query)
  )
})

async function switchOrganization(organizationId: string) {
  if (organizationId === authStore.currentOrganizationId) {
    isOpen.value = false
    return
  }
  
  isSwitching.value = true
  try {
    const success = await authStore.selectOrganization(organizationId)
    if (success) {
      uiStore.success('Switched organization successfully')
      // Allow Vue reactivity + localStorage writes to flush before hard reload
      await nextTick()
      window.location.href = '/dashboard'
    } else {
      uiStore.error('Failed to switch organization')
    }
  } catch (error) {
    console.error('Failed to switch organization:', error)
    uiStore.error('Failed to switch organization')
  } finally {
    isSwitching.value = false
    isOpen.value = false
  }
}

function toggleDropdown() {
  if (!isVisible.value) return
  isOpen.value = !isOpen.value
}

function closeDropdown() {
  isOpen.value = false
}

// Auto-focus search and clear query when dropdown opens/closes
watch(isOpen, async (open) => {
  if (open) {
    searchQuery.value = ''
    await nextTick()
    searchInputRef.value?.focus()
  }
})

// Fetch all organizations for super admins
onMounted(async () => {
  if (isSuperAdmin.value) {
    try {
      const orgs = await getOrganizations()
      allOrganizations.value = orgs.map(o => ({
        id: o.id,
        name: o.name,
      }))
    } catch (error) {
      console.error('Failed to fetch all organizations for switcher:', error)
      // Fall back to membership-based list silently
    }
  }
})
</script>

<template>
  <div 
    v-if="isVisible"
    class="organization-switcher"
    :class="{ 'organization-switcher--collapsed': collapsed }"
    @blur="closeDropdown"
    tabindex="0"
  >
    <button 
      class="organization-current"
      :class="{ 'organization-current--open': isOpen }"
      @click="toggleDropdown"
      :disabled="isSwitching"
    >
      <div class="organization-info" v-if="!collapsed">
        <span class="organization-name">{{ currentOrganization?.name || 'Select Organization' }}</span>

      </div>
      <div v-else class="organization-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        </svg>
      </div>
      <svg 
        v-if="!collapsed"
        class="chevron" 
        :class="{ 'chevron--open': isOpen }"
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        stroke-width="2"
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    </button>

    <!-- Dropdown -->
    <Transition name="dropdown">
      <div v-if="isOpen" class="organization-dropdown">
        <!-- Search Input -->
        <div class="organization-search">
          <input
            ref="searchInputRef"
            v-model="searchQuery"
            type="text"
            class="organization-search-input"
            placeholder="Search organizations..."
            @click.stop
          />
        </div>

        <!-- Org List -->
        <div class="organization-list">
          <div v-if="filteredOrganizations.length === 0" class="organization-empty">
            No organizations match "{{ searchQuery }}"
          </div>
          <button
            v-for="org in filteredOrganizations"
            :key="org.id"
            class="organization-option"
            :class="{ 'organization-option--active': org.id === currentOrganization?.id }"
            @click="switchOrganization(org.id)"
          >
            <div class="organization-option-info">
              <span class="organization-option-name">{{ org.name }}</span>

            </div>
            <svg 
              v-if="org.id === currentOrganization?.id"
              class="check-icon" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              stroke-width="2"
            >
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.organization-switcher {
  position: relative;
  margin: 0.5rem;
}

.organization-switcher--collapsed {
  margin: 0.5rem 0.25rem;
}

.organization-current {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: rgba(18, 192, 221, 0.08);
  border: 1px solid var(--border-color);
  border-left: 3px solid var(--color-primary);
  border-radius: var(--radius-md);
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.15s;
}

.organization-current:hover {
  background: rgba(18, 192, 221, 0.15);
  border-color: var(--color-primary);
  border-left-color: var(--color-primary);
}

.organization-current--open {
  border-color: var(--color-primary);
  border-left-color: var(--color-primary);
  background: rgba(18, 192, 221, 0.15);
}

.organization-current:disabled {
  opacity: 0.7;
  cursor: wait;
}

.organization-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.125rem;
  min-width: 0;
  flex: 1;
}

.organization-name {
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.organization-role {
  font-size: var(--text-xs);
  color: var(--text-muted);
  text-transform: capitalize;
}

.organization-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.organization-icon svg {
  width: 20px;
  height: 20px;
  color: var(--text-secondary);
}

.chevron {
  width: 16px;
  height: 16px;
  color: var(--text-muted);
  flex-shrink: 0;
  transition: transform 0.2s;
}

.chevron--open {
  transform: rotate(180deg);
}

/* Dropdown */
.organization-dropdown {
  position: absolute;
  bottom: calc(100% + 4px);
  left: 0;
  right: 0;
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  display: flex;
  flex-direction: column;
}

/* Dark mode explicit styling */
:root[data-theme="dark"] .organization-dropdown {
  background: var(--bg-elevated);
}

/* Search */
.organization-search {
  padding: 0.5rem;
  border-bottom: 1px solid var(--border-color);
}

.organization-search-input {
  width: 100%;
  padding: 0.375rem 0.5rem;
  background: var(--input-bg);
  border: 1px solid var(--input-border);
  border-radius: var(--radius-sm);
  color: var(--text-primary);
  font-size: var(--text-xs);
  outline: none;
  transition: border-color 0.15s;
}

.organization-search-input::placeholder {
  color: var(--text-disabled);
}

.organization-search-input:focus {
  border-color: var(--color-primary);
}

/* Scrollable list */
.organization-list {
  max-height: 240px;
  overflow-y: auto;
}

.organization-empty {
  padding: 0.75rem;
  font-size: var(--text-xs);
  color: var(--text-muted);
  text-align: center;
}

.organization-option {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.625rem 0.75rem;
  background: transparent;
  border: none;
  color: var(--text-primary);
  cursor: pointer;
  transition: background 0.15s;
  text-align: left;
}

.organization-option:hover {
  background: var(--bg-hover);
}

.organization-option--active {
  background: var(--color-info-bg);
}

.organization-option:not(:last-child) {
  border-bottom: 1px solid var(--border-subtle);
}

.organization-option-info {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.organization-option-name {
  font-size: var(--text-sm);
  font-weight: 500;
}

.organization-option-role {
  font-size: var(--text-xs);
  color: var(--text-muted);
  text-transform: capitalize;
}

.check-icon {
  width: 16px;
  height: 16px;
  color: var(--color-primary);
  flex-shrink: 0;
}

/* Transitions */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 0.15s, transform 0.15s;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(4px);
}
</style>
